import React, { Dispatch, SetStateAction, useState } from 'react'

// ** MUI Imports
import { makeStyles } from '@material-ui/core/styles'
import InputBase, { InputBaseProps } from '@mui/material/InputBase'
import { Divider, IconButton, Menu, MenuItem, Paper, Typography, useTheme } from '@mui/material'
import CloseIcon from 'mdi-material-ui/CloseCircle'
import MenuIcon from 'mdi-material-ui/ArrowDownDropCircle'
import { Search, ChevronDown, ArrowRight } from 'react-feather'
import TextFieldError from '../../form-elements/TextFieldError'

// ** To Do ** //
// SearchBar component can be improved

/**
 * Renders search bar.
 *
 * @param  {string} searchText                                      Searched Text
 * @param  {Dispatch<SetStateAction<string>>} onChange              To update searched text on key presses
 * @param  {} handleSearchSubmit                                    To submit search
 * @param  {} handleReset                                            To clear search
 * @param  {string} placeholder                                     Placeholder for searched field
 */

const useStyles = makeStyles(theme => ({
  root: {
    border: `2px solid #F0F0F0 !important`
  },
  focused: {
    border: `2px solid #01CCA7 !important`
  }
}))

interface SearchBarProps {
  searchText: string | undefined
  onChange: Dispatch<SetStateAction<string>> | undefined
  label?: string | undefined
  placeholder: string | undefined
  handleSearchSubmit: (() => void) | undefined
  clearSearch: ((value: SetStateAction<string>) => void) | undefined
  selectSearchParameter?: ((value: string) => void) | undefined
  hideSearchBy?: boolean
  errorStatus?: boolean
  errorMessage?: string
  handleFocus?: (e: any) => void
  searchParameters?: any
  customCss?: string
  autoFocus?: boolean
  submitHandlerRequired?: boolean
  inputBaseProps?: InputBaseProps
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchText,
  onChange,
  label,
  placeholder,
  handleSearchSubmit,
  clearSearch,
  selectSearchParameter,
  hideSearchBy = false,
  errorStatus = false,
  errorMessage,
  handleFocus,
  searchParameters,
  customCss,
  autoFocus = true,
  submitHandlerRequired = true,
  inputBaseProps
}) => {
  // ** Hook
  const theme = useTheme()
  const classes = useStyles(theme)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const parameterMenuOpen = Boolean(anchorEl)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key == 'Enter') {
      event.preventDefault()
      typeof handleSearchSubmit !== 'undefined' && handleSearchSubmit()
    }
  }

  return (
    <>
      <InputBase
        type='text'
        classes={{ focused: classes.focused }}
        autoFocus={autoFocus}
        placeholder={placeholder}
        inputProps={{ 'aria-label': 'search' }}
        value={searchText}
        error={errorStatus}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          typeof onChange !== 'undefined' && onChange(event.target.value as string)
        }}
        className={`flex items-center h-[38px] w-fit text-sm border border-gray-light transition duration-200 hover:border-coal-lighter overflow-hidden !bg-[#FFF] rounded bg-gray-lighter min-w-[460px] ${customCss}`}
        startAdornment={
          <>
            {selectSearchParameter && !Boolean(hideSearchBy) ? (
              <div className='mr-3'>
                <div
                  className='flex items-center py-2 pl-3 pr-2 space-x-1 cursor-pointer bg-gray-lighter'
                  onClick={event => setAnchorEl(event.currentTarget)}
                >
                  <Search className='w-4 h-4' />
                  <Typography className='font-medium whitespace-nowrap' variant='subtitle2'>
                    by {label}
                  </Typography>
                  <ChevronDown className='w-4 h-4' />
                </div>
                <Menu
                  id='basic-menu'
                  anchorEl={anchorEl}
                  open={parameterMenuOpen}
                  onClose={() => setAnchorEl(null)}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button'
                  }}
                >
                  {searchParameters?.map((parameter: any, index: number) => {
                    return (
                      <MenuItem
                        key={parameter?.value}
                        onClick={() => {
                          selectSearchParameter(parameter?.value)
                          setAnchorEl(null)
                          typeof clearSearch !== 'undefined' && clearSearch('')
                        }}
                      >
                        {parameter?.label}
                      </MenuItem>
                    )
                  })}
                </Menu>
              </div>
            ) : (
              <div className='pl-3 pr-1'>
                <Search className='w-4 h-4' />
              </div>
            )}
          </>
        }
        endAdornment={
          <div
            className={`flex ml-3 transition-all duration-200 ${
              searchText && searchText?.length > 0 ? '' : 'translate-x-full'
            }`}
          >
            {searchText && searchText?.length && (
              <>
                <button
                  color='primary'
                  className='p-2 text-sm font-medium text-primary-dark'
                  aria-label='clear'
                  onClick={() => typeof clearSearch !== 'undefined' && clearSearch('')}
                >
                  Clear
                </button>
                {submitHandlerRequired && (
                  <button
                    id='search-bar-button'
                    type='button'
                    aria-label='search'
                    className={`p-2 bg-gray-lighter`}
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      event.preventDefault()
                      typeof handleSearchSubmit !== 'undefined' && handleSearchSubmit()
                    }}
                  >
                    <ArrowRight className='w-4 h-4' id='search-bar-button-icon' />
                  </button>
                )}
              </>
            )}
          </div>
        }
        {...inputBaseProps}
      />
      {errorMessage ? <TextFieldError message={errorMessage ?? ''} /> : <></>}
    </>
  )
}

export default SearchBar
